import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailDpSensor } from '../../dto/trail-dp-sensor';
import { TrailDpSensorService } from '../../service/trail-dp-sensor.service';
import { Trail } from '../../dto/trail';
import { TrailDpSensorDetailComponent } from '../trail-dp-sensor-detail/trail-dp-sensor-detail.component';

@Component({
  selector: 'dpi-trail-dp-sensor',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-dp-sensor.component.html',
  styleUrl: './trail-dp-sensor.component.scss'
})
export class TrailDpSensorComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['name', 'records_checked', 'remark'];
  dataSource: TrailDpSensor[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailDpSensorService: TrailDpSensorService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailDpSensorService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.records_checked) {
              obj.records_check_text = 'Yes';
            } else {
              obj.records_check_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailDpSensor(selected: TrailDpSensor) {
    if (!this.isReadonly) {
      var objCopy: TrailDpSensor = { ...selected };
      const dialogRef = this._dialog.open(TrailDpSensorDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailDpSensor);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailDpSensor.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailDpSensorService.createDpSensor(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailDpSensor(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailDpSensor) {
    let list: TrailDpSensor[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailDpSensor) {
    this.trailDpSensorService.updateDpSensor(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailDpSensor) {
    let list: TrailDpSensor[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        if (model.records_checked) {
          model.records_check_text = 'Yes';
        } else {
          model.records_check_text = 'No';
        }
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailDpSensorService.deleteDpSensor(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailDpSensor[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

}

